import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { NodeData } from '../types/workflow';

// Only these action types have a Play button in Settings
const PLAYABLE_TYPES = ['click', 'hover', 'write', 'get_element', 'get_elements', 'switch_to_frame', 'switch_to_new_window', 'code', 'loop', 'api_call', 'webhook', 'last_file', 'rename_file', 'create_file', 'read_file', 'ai_agent'];
const SESSION_INDEPENDENT_TYPES = ['api_call', 'webhook', 'code', 'last_file', 'rename_file', 'create_file', 'read_file', 'ai_agent'];

const CustomNode: React.FC<NodeProps<NodeData>> = ({ data, id }) => {
    const getNodeColor = (type: string) => {
        const colors: Record<string, string> = {
            open_browser: '#1B5E20',
            close_browser: '#B71C1C',
            click: '#4CAF50',
            hover: '#66BB6A',
            write: '#2196F3',
            sleep: '#FF9800',
            get_element: '#9C27B0',
            get_elements: '#9C27B0',
            while: '#F44336',
            loop: '#E91E63',
            if: '#FFC107',
            api_call: '#00BCD4',
            switch_to_new_window: '#607D8B',
            switch_to_frame: '#795548',
            code: '#37474F',
            webhook: '#00695C',
            last_file: '#8D6E63',
            rename_file: '#6D4C41',
            wait_for_url_change: '#FF9800',
            wait_for_element: '#FF9800',
            create_file: '#5D4037',
            read_file: '#A1887F',
            ai_agent: '#6200EA',
        };
        return colors[type] || '#757575';
    };

    const isPlayable = PLAYABLE_TYPES.includes(data.action.type);
    const playStatus = data.playStatus || 'idle';
    const needsSession = !SESSION_INDEPENDENT_TYPES.includes(data.action.type);
    const canPlay = isPlayable && !!data.onPlay && (needsSession ? !!data.interactiveSessionId : true);

    const playBtnStyle: React.CSSProperties = {
        padding: '2px 8px',
        fontSize: '10px',
        cursor: canPlay && playStatus !== 'playing' ? 'pointer' : 'not-allowed',
        border: '1px solid white',
        borderRadius: '3px',
        color: 'white',
        fontWeight: 'bold',
        background: playStatus === 'playing'
            ? '#888'
            : canPlay
                ? 'rgba(76,175,80,0.7)'
                : 'rgba(255,255,255,0.15)',
        opacity: canPlay ? 1 : 0.5,
        transition: 'background 0.2s',
    };

    return (
        <div
            style={{
                padding: '10px 15px',
                borderRadius: '8px',
                background: getNodeColor(data.action.type),
                color: 'white',
                minWidth: '150px',
                maxWidth: '250px',
                border: playStatus === 'success'
                    ? '3px solid #69f0ae'
                    : playStatus === 'error'
                        ? '3px solid #ff5252'
                        : '2px solid #fff',
                boxShadow: playStatus === 'success'
                    ? '0 0 8px rgba(105,240,174,0.6)'
                    : playStatus === 'error'
                        ? '0 0 8px rgba(255,82,82,0.6)'
                        : '0 2px 8px rgba(0,0,0,0.2)',
            }}
        >
            <Handle type="target" position={Position.Top} />
            <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '11px', textTransform: 'uppercase' }}>
                {data.action.type.replace(/_/g, ' ')}
            </div>
            <div style={{ fontSize: '12px', opacity: 0.9, wordBreak: 'break-word' }}>
                {data.label}
            </div>
            {(data.action.type === 'while' || data.action.type === 'loop' || data.action.type === 'if') && (
                <div style={{ fontSize: '10px', marginTop: '5px', opacity: 0.9 }}>
                    📦 {data.action.actions?.length || 0} nested action(s)
                </div>
            )}
            {data.onEdit && data.onDelete && (
                <div style={{ marginTop: '8px', display: 'flex', gap: '5px', flexDirection: 'column' }}>
                    {(data.action.type === 'while' || data.action.type === 'loop' || data.action.type === 'if') && (
                        <button
                            onClick={() => data.onEdit?.(id)}
                            style={{
                                padding: '4px 8px',
                                fontSize: '10px',
                                cursor: 'pointer',
                                background: 'rgba(255,255,255,0.3)',
                                border: '1px solid white',
                                borderRadius: '3px',
                                color: 'white',
                                fontWeight: 'bold',
                            }}
                        >
                            ✏️ Edit Nested Actions
                        </button>
                    )}
                    <div style={{ display: 'flex', gap: '5px' }}>
                        <button
                            onClick={() => data.onEdit?.(id)}
                            style={{
                                padding: '2px 8px',
                                fontSize: '10px',
                                cursor: 'pointer',
                                background: 'rgba(255,255,255,0.2)',
                                border: '1px solid white',
                                borderRadius: '3px',
                                color: 'white',
                                flex: 1,
                            }}
                        >
                            ⚙️ Settings
                        </button>
                        {isPlayable && (
                            <button
                                onClick={() => canPlay && playStatus !== 'playing' && data.onPlay?.(id)}
                                disabled={!canPlay || playStatus === 'playing'}
                                title={
                                    !data.interactiveSessionId
                                        ? 'Open an interactive browser first'
                                        : playStatus === 'playing'
                                            ? 'Executing...'
                                            : playStatus === 'success'
                                                ? '✅ Last run succeeded — click to run again'
                                                : playStatus === 'error'
                                                    ? '❌ Last run failed — open Settings for details'
                                                    : 'Run this action in interactive browser'
                                }
                                style={playBtnStyle}
                            >
                                {playStatus === 'playing' ? '⏳' : '▶'}
                            </button>
                        )}
                        <button
                            onClick={() => data.onDelete?.(id)}
                            style={{
                                padding: '2px 8px',
                                fontSize: '10px',
                                cursor: 'pointer',
                                background: 'rgba(255,255,255,0.2)',
                                border: '1px solid white',
                                borderRadius: '3px',
                                color: 'white',
                            }}
                        >
                            🗑️
                        </button>
                    </div>
                </div>
            )}
            <Handle type="source" position={Position.Bottom} />
        </div>
    );
};

export default CustomNode;
